import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  HelpCircle, 
  Book, 
  MessageCircle,
  Phone,
  Mail,
  Download,
  ExternalLink,
  Search,
  FileText,
  Video,
  Users,
  Shield,
  Database,
  Globe
} from 'lucide-react';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface GuideItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  type: 'pdf' | 'video' | 'article';
}

export const HelpCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const faqs: FAQItem[] = [
    {
      id: '1',
      question: 'Cum adaug un pacient nou în sistem?',
      answer: 'Pentru a adăuga un pacient nou, navigați la secțiunea "Caz nou" din meniul principal. Completați toate câmpurile obligatorii marcate cu asterisc roșu, inclusiv datele demografice și informațiile medicale inițiale. După completare, apăsați butonul "Salvează" pentru a înregistra pacientul în sistem.',
      category: 'patients'
    },
    {
      id: '2',
      question: 'Ce sunt codurile ICD-10 și cum le utilizez?',
      answer: 'Codurile ICD-10 sunt un sistem internațional de clasificare a bolilor. Pentru malformațiile cardiace congenitale, utilizăm codurile din categoria Q20-Q28. Sistemul vă va sugera automat codurile relevante când introduceți diagnosticul. Asigurați-vă că selectați codul cel mai specific pentru diagnosticul pacientului.',
      category: 'medical'
    },
    {
      id: '3',
      question: 'Cum generez un raport EUROCAT?',
      answer: 'Rapoartele EUROCAT pot fi generate din secțiunea "Rapoarte EUROCAT" disponibilă pentru administratori. Selectați perioada de raportare, apoi apăsați "Generează raport". Sistemul va valida automat datele și vă va indica eventualele erori care trebuie corectate înainte de trimitere.',
      category: 'reports'
    },
    {
      id: '4',
      question: 'Cum îmi schimb parola?',
      answer: 'Pentru a schimba parola, accesați profilul dvs. din colțul din dreapta sus al ecranului. În tab-ul "Securitate", veți găsi opțiunea "Schimbă parola". Introduceți parola curentă și noua parolă de două ori pentru confirmare.',
      category: 'account'
    },
    {
      id: '5',
      question: 'Ce fac dacă sistemul nu răspunde?',
      answer: 'Dacă sistemul nu răspunde, încercați mai întâi să reîmprospătați pagina (F5). Dacă problema persistă, verificați conexiunea la internet. Pentru probleme tehnice persistente, contactați administratorul sistemului la support@chdregistry.ro.',
      category: 'technical'
    },
    {
      id: '6',
      question: 'Cum export date pentru analiză?',
      answer: 'Datele pot fi exportate din secțiunea "Rapoarte". Selectați tipul de raport dorit, perioada și formatul (Excel, CSV sau PDF). Pentru exporturi mari, sistemul vă va notifica prin email când fișierul este gata pentru descărcare.',
      category: 'reports'
    },
    {
      id: '7',
      question: 'Care sunt nivelurile de acces în sistem?',
      answer: 'Există trei niveluri principale de acces: 1) Utilizator Maternitate - poate adăuga cazuri noi și vizualiza date de bază, 2) Utilizator Spital - acces complet la datele medicale din spitalul propriu, 3) Administrator - acces complet la sistem, inclusiv raportare EUROCAT și configurări.',
      category: 'account'
    },
    {
      id: '8',
      question: 'Cum adaug date despre urmărirea pe termen lung?',
      answer: 'Datele de urmărire se adaugă din fișa pacientului, secțiunea "Urmărire". Selectați data vizitei și completați markerii de sănătate relevanți. Sistemul vă va reaminti automat despre vizitele de urmărire programate la 1, 5 și 10 ani.',
      category: 'medical'
    }
  ];

  const guides: GuideItem[] = [
    {
      title: 'Ghid de utilizare complet',
      description: 'Manual detaliat pentru toate funcționalitățile sistemului',
      icon: <Book className="h-5 w-5" />,
      link: '/docs/user-guide.pdf',
      type: 'pdf'
    },
    {
      title: 'Tutorial video - adăugare pacient',
      description: 'Învățați pas cu pas cum să adăugați un pacient nou',
      icon: <Video className="h-5 w-5" />,
      link: '/videos/add-patient',
      type: 'video'
    },
    {
      title: 'Clasificarea EUROCAT',
      description: 'Ghid pentru codificarea corectă conform standardelor EUROCAT',
      icon: <Globe className="h-5 w-5" />,
      link: '/docs/eurocat-classification.pdf',
      type: 'pdf'
    },
    {
      title: 'Securitate și GDPR',
      description: 'Politici de securitate și conformitate GDPR',
      icon: <Shield className="h-5 w-5" />,
      link: '/docs/security-gdpr.pdf',
      type: 'pdf'
    },
    {
      title: 'Import/export date',
      description: 'Cum să importați și exportați date în sistem',
      icon: <Database className="h-5 w-5" />,
      link: '/docs/data-import-export.pdf',
      type: 'pdf'
    },
    {
      title: 'Raportare și statistici',
      description: 'Generarea și interpretarea rapoartelor',
      icon: <FileText className="h-5 w-5" />,
      link: '/docs/reporting.pdf',
      type: 'pdf'
    }
  ];

  const categories = [
    { value: 'all', label: 'Toate' },
    { value: 'patients', label: 'Pacienți' },
    { value: 'medical', label: 'Medical' },
    { value: 'reports', label: 'Rapoarte' },
    { value: 'account', label: 'Cont' },
    { value: 'technical', label: 'Tehnic' }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Centru de ajutor</h1>
          <p className="text-muted-foreground">
            Găsiți răspunsuri rapide și resurse utile
          </p>
        </div>

        {/* Search Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Căutați în întrebări frecvente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Documentație</CardTitle>
              <Book className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">6</div>
              <p className="text-xs text-muted-foreground">Ghiduri disponibile</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tutoriale video</CardTitle>
              <Video className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">12</div>
              <p className="text-xs text-muted-foreground">Ore de conținut</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Întrebări frecvente</CardTitle>
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{faqs.length}</div>
              <p className="text-xs text-muted-foreground">Răspunsuri disponibile</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suport</CardTitle>
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">24/7</div>
              <p className="text-xs text-muted-foreground">Asistență disponibilă</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* FAQs Section - 2 columns */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <HelpCircle className="mr-2 h-5 w-5" />
                    Întrebări frecvente
                  </CardTitle>
                  <div className="flex gap-2">
                    {categories.map(cat => (
                      <Badge
                        key={cat.value}
                        variant={selectedCategory === cat.value ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => setSelectedCategory(cat.value)}
                      >
                        {cat.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {filteredFAQs.map(faq => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <AccordionTrigger className="text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground">{faq.answer}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                {filteredFAQs.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">
                    Nu s-au găsit întrebări care să corespundă căutării.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Resources Section - 1 column */}
          <div className="space-y-6">
            {/* Guides */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Book className="mr-2 h-5 w-5" />
                  Ghiduri și resurse
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {guides.map((guide, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-accent cursor-pointer"
                  >
                    <div className="mt-1">{guide.icon}</div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{guide.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        {guide.description}
                      </p>
                    </div>
                    {guide.type === 'pdf' && (
                      <Download className="h-4 w-4 text-muted-foreground" />
                    )}
                    {guide.type === 'video' && (
                      <Video className="h-4 w-4 text-muted-foreground" />
                    )}
                    {guide.type === 'article' && (
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Contact Support */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Contactați Suportul
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Nu ați găsit răspunsul? Echipa noastră de suport este aici să vă ajute.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">support@chdregistry.ro</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">+40 21 123 4567</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Luni - Vineri, 9:00 - 17:00</span>
                    </div>
                  </div>
                </div>
                <Button className="w-full">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Deschide tichet suport
                </Button>
              </CardContent>
            </Card>

            {/* Training Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="mr-2 h-5 w-5" />
                  Sesiuni de training
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Participați la sesiunile noastre de training online pentru a învăța cum să utilizați sistemul eficient.
                </p>
                <div className="space-y-2">
                  <div className="p-2 border rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Training utilizatori noi</span>
                      <Badge>Marți, 14:00</Badge>
                    </div>
                  </div>
                  <div className="p-2 border rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Raportare EUROCAT</span>
                      <Badge>Joi, 11:00</Badge>
                    </div>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-4">
                  Înregistrare training
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};